import Text from "@/components/ui/text";
import React from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  TooltipProps,
  Legend
} from "recharts";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { isEmpty } from "lodash";
import {
  DreamerLineChartDataType,
  LineConfig
} from "@/components/DreamerCharts/types";
import { formatNumber } from "@/utils/numbers";
import LegendWithMore from "@/components/DreamerCharts/LegendWithMore";

const formatLineValue = (raw: number, isPercentage?: boolean) => {
  const { value, modifier } = formatNumber(raw, 2);
  return `${value}${modifier}${isPercentage ? "%" : ""}`;
};

interface Props {
  height: number | string;
  chartData: DreamerLineChartDataType[];
  lines: LineConfig[];
  tooltipYpos?: number;
  isPercentage?: boolean;
  chartClassName?: string;
  legendClassName?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  yAxisDomain?: [number, number];
  compact?: boolean;
}

const LineTooltip = ({
  active,
  payload,
  lines,
  isPercentage
}: TooltipProps<ValueType, NameType> & {
  lines: LineConfig[];
  isPercentage?: boolean;
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as DreamerLineChartDataType;

    return (
      <div className="bg-surface-elevation3 border border-border-border1 rounded-lg p-2 shadow-lg">
        <Text variant="smBody" className="text-primary font-semibold mb-1">
          {data.label}
        </Text>
        {lines.map((line) => {
          const value = data[line.dataKey];
          const numValue = typeof value === "number" ? value : 0;
          return (
            <div key={line.dataKey} className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: line.color }}
              />
              <Text variant="smBody" className="text-secondary">
                {line.label}: {formatLineValue(numValue, isPercentage)}
              </Text>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

const DreamerLineChart: React.FC<Props> = ({
  height,
  chartData,
  lines,
  tooltipYpos = 10,
  isPercentage = true,
  chartClassName,
  legendClassName,
  showLegend = true,
  showGrid = true,
  yAxisDomain,
  compact = false
}) => {
  if (isEmpty(chartData)) return null;

  const showOnlyEndLabels = compact && chartData.length > 6;
  const lastIndex = chartData.length - 1;

  const containerHeight = typeof height === "number" ? `${height}px` : height;
  return (
    <div className="flex flex-col w-full" style={{ height: containerHeight }}>
      <div className="relative flex-1 min-h-0 w-full">
        <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart
          data={chartData}
          margin={{ left: 25, right: 25, top: 10 }}
          className={chartClassName}
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="5 5"
              stroke="var(--widget)"
              horizontal={false}
            />
          )}

          <XAxis
            dataKey="label"
            interval={0}
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={
              showOnlyEndLabels
                ? (props: {
                    x: number;
                    y: number;
                    payload: { value: string };
                    index: number;
                  }) => {
                    if (props.index !== 0 && props.index !== lastIndex) {
                      return <g />;
                    }
                    return (
                      <text
                        x={props.x}
                        y={props.y}
                        dy="0.71em"
                        textAnchor="middle"
                        fill="var(--secondary)"
                        fontSize={10}
                      >
                        {props.payload.value}
                      </text>
                    );
                  }
                : { fill: "var(--secondary)", fontSize: 10 }
            }
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={{ fill: "var(--secondary)", fontSize: 10 }}
            tickFormatter={(tick) =>
              formatLineValue(Number(tick), isPercentage)
            }
            width={30}
            domain={yAxisDomain}
          />

          {lines.map((line) => (
            <Line
              key={line.dataKey}
              type="linear"
              dataKey={line.dataKey}
              stroke={line.color}
              strokeWidth={line.strokeWidth}
              activeDot={false}
              dot={
                line.dotSize
                  ? { fill: line.color, r: line.dotSize }
                  : { fill: line.color }
              }
            />
          ))}

          {/* cursor={false} suppresses the SVG crosshair; position.y pins vertical placement while Recharts auto-bounds x */}
          <RechartsTooltip
            content={<LineTooltip lines={lines} isPercentage={isPercentage} />}
            cursor={false}
            position={{ y: tooltipYpos }}
          />
          <Legend
            content={() => (
              <div className="absolute -bottom-3 left-1/2 w-full">
                <Text
                  as="div"
                  variant="smBodySecondary"
                >{`${chartData[0].label}-${chartData[chartData.length - 1].label}`}</Text>
              </div>
            )}
            verticalAlign="bottom"
            align="center"
          />
        </RechartsLineChart>
        </ResponsiveContainer>
      </div>

      {showLegend && (
        <LegendWithMore
          items={lines.map((line) => ({
            key: line.dataKey,
            label: line.label,
            color: line.color
          }))}
          className={legendClassName}
        />
      )}
    </div>
  );
};

export default DreamerLineChart;
